import { useState } from 'react'
import type { PhotoStore } from '@application/ports.ts'
import type { DayKind } from '@domain/calendar.ts'
import type { DomainEvent } from '@domain/events.ts'
import type { FamilyState, Purchase, Task } from '@domain/state.ts'
import { childrenOf, dayOf, memberDayOf, taskDayOf } from '@domain/state.ts'
import type { PurchaseId, TaskId } from '@domain/ids.ts'
import { es } from '../strings/es.ts'
import { usePhoto } from '../child/usePhoto.ts'

const DAY_KINDS: readonly DayKind[] = [
  'weekday',
  'weekend',
  'holiday',
  'vacation',
  'special',
  'sick',
]

interface Pending {
  readonly task: Task
  readonly memberName: string
  readonly photoId: string | undefined
  /** La hora a la que el niño la marcó, «HH:MM», o vacío si no se sabe. */
  readonly marcadaA: string
}

/**
 * A qué hora marcó el niño la tarea, sacada del sello del evento.
 *
 * Se lee el texto ISO tal cual y no se pasa por `Date`, por la misma razón por
 * la que se hizo así en «cómo va cada uno»: el sello ya lleva el desfase del
 * sitio donde se marcó, y reconstruirlo daría la hora de Londres.
 */
function horaDeMarcado(events: readonly DomainEvent[], day: string, taskId: TaskId): string {
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i]
    if (event?.type !== 'TaskMarkedDone') continue
    if (event.day !== day || event.payload.taskId !== taskId) continue
    return /T(\d{2}:\d{2})/.exec(event.at)?.[1] ?? ''
  }
  return ''
}

function PhotoThumb({ photos, id }: { photos: PhotoStore | null; id: string | undefined }) {
  const url = usePhoto(photos, id as never)
  if (!url) return null
  return <img src={url} alt="" className="mt-3 max-h-56 w-full rounded-lg object-cover" />
}

/**
 * What a parent does, in one place: confirm what is done, say which minutes were
 * actually granted in Family Link, and mark the shape of the day.
 *
 * Validating is one tap. If it is not done, the app says which step is missing;
 * the parent does not have to.
 */
export function ParentMode({
  state,
  events,
  day,
  photos,
  onApprove,
  onReject,
  onUndo,
  onGrant,
  onDeliver,
  onDeclareDay,
  onPause,
  onInsights,
  onConfig,
  onShop,
  onPorQue,
  onCalendario,
  onSemaforo,
  onAssistant,
  onSystem,
  onExit,
}: {
  state: FamilyState
  /** El registro en crudo, para leer a qué hora se marcó cada cosa. */
  events: readonly DomainEvent[]
  day: string
  photos: PhotoStore | null
  onApprove: (taskId: TaskId) => Promise<string | null>
  onReject: (taskId: TaskId, note: string) => Promise<string | null>
  onUndo: (taskId: TaskId, reason: string) => Promise<string | null>
  onGrant: (purchaseId: PurchaseId) => Promise<string | null>
  onDeliver: (purchaseId: PurchaseId) => Promise<string | null>
  onDeclareDay: (kind: DayKind) => void
  onPause: (memberId: string) => void
  onInsights: () => void
  onConfig: () => void
  onShop: () => void
  onPorQue: () => void
  onCalendario: () => void
  onSemaforo: () => void
  /** Undefined when this build has no assistant configured, which hides it. */
  onAssistant: (() => void) | undefined
  onSystem: () => void
  onExit: () => void
}) {
  const [rejecting, setRejecting] = useState<TaskId | null>(null)
  const [undoing, setUndoing] = useState<TaskId | null>(null)
  const [note, setNote] = useState('')
  const [message, setMessage] = useState<string | null>(null)

  const pending: Pending[] = []
  for (const task of state.tasks.values()) {
    if (task.archived) continue
    const today = taskDayOf(state, day as never, task.memberId, task.id)
    if (today.status !== 'marked') continue
    pending.push({
      task,
      memberName: state.members.get(task.memberId)?.name ?? '',
      photoId: today.photoId,
      marcadaA: horaDeMarcado(events, day, task.id),
    })
  }

  const checked: Pending[] = []
  for (const task of state.tasks.values()) {
    if (task.archived) continue
    const today = taskDayOf(state, day as never, task.memberId, task.id)
    if (today.status !== 'validated') continue
    checked.push({
      task,
      marcadaA: '',
      memberName: state.members.get(task.memberId)?.name ?? '',
      photoId: today.photoId,
    })
  }

  // Delivered is finished; cancelled is a child who changed their mind and
  // already has the coins back. Neither is anything for a parent to do, and a
  // cancelled one sitting in this list would only get tapped and refused.
  // Anything still unresolved, whatever day it was bought on. Filtering by
  // today used to hide a purchase the moment the day rolled over, leaving the
  // coins spent and nobody able to grant it or give them back.
  const grants: Purchase[] = [...state.purchases.values()].filter(
    (p) =>
      p.status !== 'delivered' &&
      p.status !== 'cancelled' &&
      (p.day === day || p.status === 'pending'),
  )

  const say = async (action: Promise<string | null>): Promise<void> => {
    setMessage(await action)
  }

  const children = childrenOf(state)
  const currentKind = dayOf(state, day as never).kind

  return (
    <div className="mx-auto max-w-2xl px-5 pt-5 pb-16">
      <header className="mb-6 flex items-center justify-between gap-4">
        <h1 className="m-0 text-2xl font-extrabold tracking-tight">{es.parent.title}</h1>
        <button type="button" onClick={onExit} className="text-muted text-sm font-bold">
          {es.parent.exit}
        </button>
      </header>

      {message ? (
        <p role="status" className="bg-coral-soft text-coral mb-5 rounded-lg p-3 font-bold">
          {message}
        </p>
      ) : null}

      <section className="mb-10">
        <h2 className="text-muted mb-3 text-xs font-bold tracking-[0.1em] uppercase">
          {es.parent.validate}
        </h2>
        {pending.length === 0 ? (
          <p className="text-muted m-0">{es.parent.validateEmpty}</p>
        ) : (
          <ul className="grid list-none gap-3 p-0">
            {pending.map(({ task, memberName, photoId, marcadaA }) => (
              <li key={task.id} className="border-rule bg-surface m-0 rounded-xl border p-4">
                <p className="m-0 text-lg font-bold">
                  {task.emoji} {task.name}
                </p>
                <p className="text-muted m-0 mt-1 text-sm font-bold">
                  {memberName} · {es.parent.pays(task.rewards[task.rung].coins)}
                  {marcadaA ? ` · ${es.parent.markedAt(marcadaA)}` : ''}
                </p>

                {/*
                  Antes aquí solo estaba el título, y validar era decir «sí» a
                  una palabra. Los pasos son lo que el niño dijo que hizo, uno
                  por uno: es lo que convierte el toque en una comprobación y no
                  en un trámite. Y para el niño que discute lo que es «bien
                  hecho», es lo que hay delante cuando se habla de ello.
                */}
                {task.steps.length > 0 ? (
                  <ol className="text-muted mt-3 mb-0 grid list-none gap-1 p-0 text-sm">
                    {task.steps.map((step) => (
                      <li key={step.id} className="m-0 flex items-start gap-2">
                        <span aria-hidden="true" className="text-mint font-bold">
                          ✓
                        </span>
                        {step.text}
                      </li>
                    ))}
                  </ol>
                ) : null}

                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {task.referencePhotoId !== undefined ? (
                    <figure className="m-0">
                      <figcaption className="text-muted mb-1 text-xs font-bold uppercase">
                        {es.parent.reference}
                      </figcaption>
                      <PhotoThumb photos={photos} id={task.referencePhotoId} />
                    </figure>
                  ) : null}
                  {photoId !== undefined ? (
                    <figure className="m-0">
                      <figcaption className="text-muted mb-1 text-xs font-bold uppercase">
                        {es.parent.result}
                      </figcaption>
                      <PhotoThumb photos={photos} id={photoId} />
                    </figure>
                  ) : null}
                </div>

                {rejecting === task.id ? (
                  <div className="mt-3">
                    <label className="mb-1 block text-sm font-bold" htmlFor={`note-${task.id}`}>
                      {es.parent.rejectNote}
                    </label>
                    <input
                      id={`note-${task.id}`}
                      className="border-rule mb-3 w-full rounded-lg border px-3"
                      value={note}
                      onChange={(e) => {
                        setNote(e.target.value)
                      }}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="bg-coral rounded-lg px-5 font-bold text-white"
                        onClick={() => {
                          void say(onReject(task.id, note))
                          setRejecting(null)
                          setNote('')
                        }}
                      >
                        {es.parent.reject}
                      </button>
                      <button
                        type="button"
                        className="border-rule rounded-lg border px-5 font-bold"
                        onClick={() => {
                          setRejecting(null)
                        }}
                      >
                        {es.pin.cancel}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      className="bg-mint flex-1 rounded-lg px-5 font-bold text-white"
                      onClick={() => {
                        void say(onApprove(task.id))
                      }}
                    >
                      {es.parent.approve}
                    </button>
                    <button
                      type="button"
                      className="border-rule rounded-lg border px-5 font-bold"
                      onClick={() => {
                        setRejecting(task.id)
                      }}
                    >
                      {es.parent.reject}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mb-10">
        <h2 className="text-muted mb-3 text-xs font-bold tracking-[0.1em] uppercase">
          {es.parent.checked}
        </h2>
        {checked.length === 0 ? (
          <p className="text-muted m-0">{es.parent.checkedEmpty}</p>
        ) : (
          <ul className="grid list-none gap-2 p-0">
            {checked.map(({ task, memberName }) => (
              <li key={task.id} className="border-rule bg-surface m-0 rounded-xl border p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="min-w-0 flex-1">
                    <span className="block font-bold">
                      {task.emoji} {task.name}
                    </span>
                    <span className="text-muted block text-sm">{memberName}</span>
                  </span>
                  <button
                    type="button"
                    className="border-rule rounded-lg border px-4 text-sm font-bold"
                    onClick={() => {
                      setUndoing(undoing === task.id ? null : task.id)
                    }}
                  >
                    {es.parent.undo}
                  </button>
                </div>
                {undoing === task.id ? (
                  <div className="mt-3">
                    <p className="text-muted mt-0 mb-2 text-sm">{es.parent.undoExplain}</p>
                    <label className="mb-1 block text-sm font-bold" htmlFor={`undo-${task.id}`}>
                      {es.parent.undoReason}
                    </label>
                    <input
                      id={`undo-${task.id}`}
                      className="border-rule mb-3 w-full rounded-lg border px-3"
                      value={note}
                      onChange={(e) => {
                        setNote(e.target.value)
                      }}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={note.trim().length === 0}
                        className="bg-coral rounded-lg px-5 font-bold text-white disabled:opacity-40"
                        onClick={() => {
                          void say(onUndo(task.id, note))
                          setUndoing(null)
                          setNote('')
                        }}
                      >
                        {es.parent.undo}
                      </button>
                      <button
                        type="button"
                        className="border-rule rounded-lg border px-5 font-bold"
                        onClick={() => {
                          setUndoing(null)
                        }}
                      >
                        {es.pin.cancel}
                      </button>
                    </div>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mb-10">
        <h2 className="text-muted mb-1 text-xs font-bold tracking-[0.1em] uppercase">
          {es.parent.grants}
        </h2>
        <p className="text-muted mt-0 mb-3 text-sm">{es.parent.grantsHelp}</p>
        {grants.length === 0 ? (
          <p className="text-muted m-0">{es.parent.grantsEmpty}</p>
        ) : (
          <ul className="grid list-none gap-2 p-0">
            {grants.map((purchase) => {
              const reward = state.rewards.get(purchase.rewardId)
              const member = state.members.get(purchase.memberId)
              return (
                <li
                  key={purchase.id}
                  className="border-rule bg-surface m-0 flex flex-wrap items-center gap-3 rounded-xl border p-4"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-bold">{reward?.name}</span>
                    <span className="text-muted block text-sm">{member?.name}</span>
                  </span>
                  <button
                    type="button"
                    className="bg-teal rounded-lg px-5 font-bold text-white"
                    onClick={() => {
                      void say(
                        purchase.status === 'pending'
                          ? onGrant(purchase.id)
                          : onDeliver(purchase.id),
                      )
                    }}
                  >
                    {purchase.status === 'pending' ? es.parent.granted : es.parent.deliver}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section className="mb-10">
        <h2 className="text-muted mb-3 text-xs font-bold tracking-[0.1em] uppercase">
          {es.parent.settings}
        </h2>
        <label className="mb-1 block text-sm font-bold" htmlFor="day-kind">
          {es.parent.dayKind}
        </label>
        <select
          id="day-kind"
          className="border-rule bg-surface mb-5 w-full rounded-lg border px-3"
          value={currentKind}
          onChange={(e) => {
            onDeclareDay(e.target.value as DayKind)
          }}
        >
          {DAY_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {es.days[kind]}
            </option>
          ))}
        </select>

        <p className="text-muted mt-0 mb-2 text-sm">{es.parent.pauseHelp}</p>
        <div className="flex flex-wrap gap-2">
          {children.map((child) => {
            const paused = memberDayOf(state, day as never, child.id).paused
            return (
              <button
                key={child.id}
                type="button"
                disabled={paused}
                className="border-rule rounded-lg border px-5 font-bold disabled:opacity-40"
                onClick={() => {
                  onPause(child.id)
                }}
              >
                {es.parent.pauseDay}: {child.name}
              </button>
            )
          })}
        </div>
      </section>

      <div className="flex flex-wrap gap-5">
        <button
          type="button"
          onClick={onInsights}
          className="text-teal text-sm font-bold underline underline-offset-4"
        >
          {es.parent.insights}
        </button>
        <button
          type="button"
          onClick={onConfig}
          className="text-teal text-sm font-bold underline underline-offset-4"
        >
          {es.parent.config}
        </button>
        <button
          type="button"
          onClick={onShop}
          className="text-teal text-sm font-bold underline underline-offset-4"
        >
          {es.parent.shop}
        </button>
        {onAssistant ? (
          <button
            type="button"
            onClick={onAssistant}
            className="text-teal text-sm font-bold underline underline-offset-4"
          >
            {es.parent.assistant}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onCalendario}
          className="text-teal text-sm font-bold underline underline-offset-4"
        >
          {es.parent.calendario}
        </button>
        <button
          type="button"
          onClick={onSemaforo}
          className="text-teal text-sm font-bold underline underline-offset-4"
        >
          {es.parent.semaforo}
        </button>
        <button
          type="button"
          onClick={onPorQue}
          className="text-teal text-sm font-bold underline underline-offset-4"
        >
          {es.parent.porQue}
        </button>
        <button
          type="button"
          onClick={onSystem}
          className="text-teal text-sm font-bold underline underline-offset-4"
        >
          {es.parent.system}
        </button>
      </div>
    </div>
  )
}
